import { OverlayModule } from '@angular/cdk/overlay';
import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { ClickOutsideDirective } from '@core/directives/click-outside.directive';
import { BalanceEnum } from '@core/enums/balance.enum';
import { ModalService } from '@features/modals/services/modal.service';
import { SvgIconComponent } from 'angular-svg-icon';
import { CostComponent } from '../../../core/components/cost/cost.component';
import NumberFlow from 'number-flow';
import { BalanceIconComponent } from '../../../core/components/balance-icon/balance-icon.component';
import { HeaderBalanceService } from './header-balance.service';

@Component({
  selector: 'app-header-balance',
  imports: [
    RouterLink,
    OverlayModule,
    SvgIconComponent,
    ClickOutsideDirective,
    CostComponent,
    BalanceIconComponent,
    RouterLinkActive,
  ],
  templateUrl: './header-balance.component.html',
  standalone: true,
  styleUrl: './header-balance.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HeaderBalanceComponent {
  private readonly modalService = inject(ModalService);
  public readonly headerBalanceService = inject(HeaderBalanceService);

  balance = input.required<Record<BalanceEnum, number>>();
  balanceType = input.required<keyof typeof BalanceEnum>();
  isShowBonusBalance = input<boolean>(false);

  balanceChange = output<keyof typeof BalanceEnum>();

  mainCostNumberFlow =
    viewChild.required<ElementRef<NumberFlow>>('mainCostNumberFlow');

  constructor() {
    afterRenderEffect(() => {
      const element = this.mainCostNumberFlow().nativeElement;
      if (element.value === this.balance()[this.balanceType()]) return;

      const isGreen =
        this.balance()[this.balanceType()] > (element?.value ?? 0);

      element.update(this.balance()[this.balanceType()]);

      // Remove any existing effect classes
      element.classList.remove('green-effect', 'red-effect');

      element.offsetHeight;

      // Add the new effect class
      element.classList.add(isGreen ? 'green-effect' : 'red-effect');
    });
  }

  ngAfterViewInit() {
    const element = this.mainCostNumberFlow().nativeElement;
    element.update(this.balance()[this.balanceType()]);
    element.locales = 'en-US';
    element.format = {
      minimumIntegerDigits: 1,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    };

    element.transformTiming = {
      duration: 300,
    };
    element.spinTiming = {
      duration: 300,
    };
    element.opacityTiming = {
      duration: 300,
    };
  }

  changeBalanceType(type: keyof typeof BalanceEnum) {
    this.balanceChange.emit(type);
    this.headerBalanceService.toggleBalancePopover();
  }

  openOurDeposits() {
    this.modalService.openModal('OurDepositsModal');
  }
}
